import type { BoxtopReadback, ReadbackRow } from '../lib/boxtopReadback'

// Read-back check: what BoxTop actually STORED for this job vs what we sent. Judged on BoxTop's own record,
// not on the HTTP status of the create call (which said 200 even when fields were dropped).
interface Props {
  jobRef: string
  readback: BoxtopReadback
  onClose: () => void
}

const GROUPS: ReadbackRow['group'][] = ['Parties', 'Ports', 'Cargo']

function cell(v: string | number | null): string {
  return v === null || v === '' ? '(empty)' : String(v)
}

export default function BoxtopReadbackDialog({ jobRef, readback, onClose }: Props) {
  const headline =
    readback.status === 'ok' ? { text: 'BoxTop stored the checked fields as sent', cls: 'bg-green-50 text-green-800 border-green-200 dark:bg-green-900/20 dark:text-green-300 dark:border-green-700' }
    : readback.status === 'mismatch' ? { text: `${readback.criticalMismatches} field${readback.criticalMismatches === 1 ? '' : 's'} in BoxTop differ from what we sent`, cls: 'bg-red-50 text-red-800 border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-700' }
    : { text: 'Could not read the job back from BoxTop', cls: 'bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-700' }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">BoxTop read-back — {jobRef}{readback.boxtopRef ? ` → ${readback.boxtopRef}` : ''}</h2>
        {readback.rows.some(r => r.consequence && r.status !== 'match') && (
          <div className="border-2 border-red-600 bg-red-50 dark:bg-red-900/30 text-red-900 dark:text-red-200 rounded-lg px-3 py-2 mb-3" role="alert">
            <p className="text-sm font-bold">⚠ HIGHEST ALERT — {readback.rows.filter(r => r.consequence && r.status !== 'match').map(r => r.label.replace(' linked to a BoxTop address', '')).join(' and ')} not linked</p>
            <p className="text-sm">This job cannot be taken into eCustoms in BoxTop: staff would have to link the address by hand first. Do not rely on this job for customs until it is linked.</p>
          </div>
        )}
        <div className={`border rounded-lg px-3 py-2 text-sm font-medium mb-3 ${headline.cls}`}>{headline.text}</div>
        <p className="text-xs text-gray-600 dark:text-gray-300 mb-3">
          Target: after a sync nobody has to re-pick the ports or the shipper / consignee in BoxTop. A value that is merely filled in is not enough —
          the port must be BoxTop's own port record (code + <em>NAME (LOCODE)</em>) and each party must be a linked BoxTop address.
        </p>
        {readback.status === 'unavailable' && <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">{readback.error}. The sync itself is not affected; use “Check in BoxTop” on the job to try again.</p>}

        {GROUPS.map(g => {
          const rows = readback.rows.filter(r => r.group === g)
          if (rows.length === 0) return null
          return (
            <div key={g} className="mb-4">
              <h3 className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400 mb-1">{g}</h3>
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs text-gray-500 dark:text-gray-400"><th className="py-1 pr-2 w-6"></th><th className="py-1 pr-2">Field</th><th className="py-1 pr-2">We sent</th><th className="py-1">BoxTop has</th></tr></thead>
                <tbody>
                  {rows.map(r => (
                    <tr key={r.field} className="border-t border-gray-100 dark:border-gray-700">
                      <td className="py-1 pr-2">{r.status === 'match' ? <span className="text-green-600">✓</span> : r.critical ? <span className="text-red-600">✗</span> : <span className="text-gray-400" title="Informational: BoxTop does not keep or rewrites this field">ℹ</span>}</td>
                      <td className="py-1 pr-2 text-gray-800 dark:text-gray-200">{r.label}{!r.critical && <span className="ml-1 text-xs text-gray-400">(info)</span>}{r.consequence && r.status !== 'match' && <span className="block text-xs font-semibold text-red-700 dark:text-red-400">{r.consequence}</span>}</td>
                      <td className="py-1 pr-2 font-mono text-xs text-gray-700 dark:text-gray-300 break-all">{cell(r.sent)}</td>
                      <td className={`py-1 font-mono text-xs break-all ${r.status === 'match' ? 'text-gray-700 dark:text-gray-300' : r.critical ? 'text-red-700 dark:text-red-400' : 'text-gray-500'}`}>{cell(r.actual)}{r.status === 'not_stored' ? ' — not stored' : ''}{r.status === 'not_linked' ? ' — text only, not linked (staff would have to re-pick it in BoxTop)' : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        })}

        <p className="text-xs text-gray-500 dark:text-gray-400 border-t border-gray-100 dark:border-gray-700 pt-3">
          This shows what BoxTop stored for the fields listed above, nothing more. All green does <strong>not</strong> mean the job or the customs
          declaration is correct, and fields not listed here were not checked. Checked {new Date(readback.checkedAt).toLocaleString('en-GB')}.
        </p>
        <div className="flex justify-end mt-4">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-lg bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-100">Close</button>
        </div>
      </div>
    </div>
  )
}
